import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiSearch, FiLoader } from "react-icons/fi";
import { apiGet, apiPost } from "../../../api";
import "../styles/SelectCourses.css";

export default function SelectCourses() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);

  const [course, setCourse] = useState(null);
  const [sectionKey, setSectionKey] = useState("");
  const [selectedLectureId, setSelectedLectureId] = useState("");
  const [selectedLabId, setSelectedLabId] = useState("");

  const [pendingItems, setPendingItems] = useState([]);
  const [msg, setMsg] = useState({ text: "", type: "" });

  // --- Search Debouncing ---
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (q.trim().length < 2) {
        setSuggestions([]);
        return;
      }
      try {
        setLoadingSearch(true);
        const data = await apiGet(
          `/courses/search?q=${encodeURIComponent(q.trim())}`
        );
        setSuggestions(data.courses || []);
      } catch {
        setSuggestions([]);
      } finally {
        setLoadingSearch(false);
      }
    };
    const timer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timer);
  }, [q]);

  // --- Load Full Course Details ---
  const loadCourse = async (c) => {
    setMsg({ text: "", type: "" });
    setQ(`${c.subject} ${c.number} — ${c.name}`);
    setSuggestions([]);
    try {
      const data = await apiGet(
        `/courses/${encodeURIComponent(c.subject)}/${encodeURIComponent(
          c.number
        )}`
      );
      setCourse(data);
      setSectionKey("");
      setSelectedLectureId("");
      setSelectedLabId("");
      setPendingItems([]);
    } catch (error) {
      setMsg({ text: "Failed to load course details.", type: "error" });
    }
  };

  // --- Memos for Derived State ---
  const sections = useMemo(() => course?.sections || [], [course]);
  const section = useMemo(() => {
    if (!sectionKey) return null;
    const [term, letter] = sectionKey.split("-");
    return sections.find((s) => s.term === term && s.letter === letter) || null;
  }, [sections, sectionKey]);

  const lectures = useMemo(() => {
    const lectureTypes = [
      "BLEN",
      "CLIN",
      "CORS",
      "DIRD",
      "DISS",
      "FDEX",
      "FIEL",
      "HYFX",
      "IDS",
      "INSP",
      "ISTY",
      "LECT",
      "LGCL",
      "ONCA",
      "ONLN",
      "PERF",
      "PRAC",
      "REEV",
      "REMT",
      "RESP",
      "REVP",
      "SEMR",
      "STDO",
      "THES",
      "WKSP",
    ];
    return (
      section?.offerings
        ?.filter((o) => lectureTypes.includes(o.type.toUpperCase()))
        .map((o) => ({
          id: `${o.type.toUpperCase()}-${o.number || "NA"}`,
          ...o,
        })) || []
    );
  }, [section]);

  const labOrTutr = useMemo(
    () =>
      section?.offerings
        ?.filter(
          (o) =>
            o.type.toUpperCase().startsWith("LAB") ||
            o.type.toUpperCase().startsWith("TUTR")
        )
        .map((o) => ({
          id: `${o.type.toUpperCase()}-${o.number || "NA"}`,
          ...o,
        })) || [],
    [section]
  );

  // --- Build Preview Items ---
  useEffect(() => {
    const buildPending = () => {
      if (!course || !section) return [];
      const [term] = sectionKey.split("-");
      const courseCode = `${course.subject} ${course.number}`;
      const base = { term, courseCode, section: section.letter };
      const chosen = [];

      const chosenLect = lectures.find((l) => l.id === selectedLectureId);
      if (chosenLect) {
        // ✅ **UPDATED**: Use the actual type from the offering (e.g., BLEN)
        chosenLect.meetings?.forEach((m) =>
          chosen.push({
            ...base,
            type: chosenLect.type.toUpperCase(),
            days: m.day,
            startTime: m.startTime,
            duration: m.duration,
            room: m.room,
          })
        );
      }

      const chosenLab = labOrTutr.find((l) => l.id === selectedLabId);
      if (chosenLab) {
        chosenLab.meetings?.forEach((m) =>
          chosen.push({
            ...base,
            type: chosenLab.type.toUpperCase().startsWith("TUTR")
              ? "TUTR"
              : "LAB",
            days: m.day,
            startTime: m.startTime,
            duration: m.duration,
            room: m.room,
          })
        );
      }
      return chosen;
    };
    setPendingItems(buildPending());
  }, [
    course,
    sectionKey,
    selectedLectureId,
    selectedLabId,
    lectures,
    labOrTutr,
    section,
  ]);

  const canShowPreview = useMemo(() => {
    if (!selectedLectureId) return false;
    if (labOrTutr.length > 0 && !selectedLabId) return false;
    return true;
  }, [selectedLectureId, selectedLabId, labOrTutr]);

  // --- Save to Schedule ---
  const saveAll = async () => {
    try {
      await apiPost(`/schedules/bulk`, { items: pendingItems });
      setMsg({
        text: "Saved successfully! You can continue adding courses or view your schedule.",
        type: "success",
      });
      setCourse(null);
      setQ("");
    } catch (e) {
      setMsg({
        text: e.message || "Save failed. Please try again.",
        type: "error",
      });
    }
  };

  return (
    <div className="select-courses-container page-card">
      <h2 className="search-header">Select Courses</h2>

      <div className="search-bar-wrapper">
        <FiSearch className="search-icon" />
        <input
          className="search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by code or name (e.g., EECS 3482, Calculus)"
        />
        {loadingSearch && <FiLoader className="spinner-icon" />}
      </div>

      {suggestions.length > 0 && (
        <div className="suggestions-box">
          {suggestions.map((c) => (
            <div
              key={`${c.subject}-${c.number}`}
              className="suggestion-item"
              onClick={() => loadCourse(c)}
            >
              <strong>
                {c.subject} {c.number}
              </strong>{" "}
              — {c.name}
            </div>
          ))}
        </div>
      )}

      {course && (
        <div className="course-details-form">
          <h3 className="course-title-header">
            {course.subject} {course.number} — {course.name}
          </h3>
          <div className="form-row">
            <label className="form-label">Section</label>
            <select
              className="form-select"
              value={sectionKey}
              onChange={(e) => {
                setSectionKey(e.target.value);
                setSelectedLectureId("");
                setSelectedLabId("");
              }}
            >
              <option value="">-- Choose term & section --</option>
              {sections.map((sct) => (
                <option
                  key={`${sct.term}-${sct.letter}`}
                  value={`${sct.term}-${sct.letter}`}
                >
                  {sct.term === "Y" ? "Y" : sct.term} — Section {sct.letter}
                  {sct.instructor ? ` — ${sct.instructor}` : ""}
                </option>
              ))}
            </select>
          </div>

          {!!lectures.length && (
            <div className="form-row">
              <label className="form-label">Lecture</label>
              <select
                className="form-select"
                value={selectedLectureId}
                onChange={(e) => setSelectedLectureId(e.target.value)}
              >
                <option value="">-- Choose lecture --</option>
                {lectures.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.type} {l.number || ""}{" "}
                    {l.meetings
                      ?.map((m) => `${m.day} ${m.startTime}`)
                      .join(", ")}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!!labOrTutr.length && (
            <div className="form-row">
              <label className="form-label">Lab/Tutorial</label>
              <select
                className="form-select"
                value={selectedLabId}
                onChange={(e) => setSelectedLabId(e.target.value)}
              >
                <option value="">-- Please select an option --</option>
                {labOrTutr.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.type} {l.number || ""} —{" "}
                    {l.meetings
                      ?.map((m) => `${m.day} ${m.startTime}`)
                      .join(", ")}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {canShowPreview && pendingItems.length > 0 && (
        <div className="preview-section">
          <h4>Preview</h4>
          <div className="table-wrapper">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>Term</th>
                  <th>Course</th>
                  <th>Section</th>
                  <th>Type</th>
                  <th>Day</th>
                  <th>Start</th>
                  <th>Duration</th>
                  <th>Room</th>
                </tr>
              </thead>
              <tbody>
                {pendingItems.map((p, idx) => (
                  <tr key={idx}>
                    <td>{p.term}</td>
                    <td>{p.courseCode}</td>
                    <td>{p.section}</td>
                    <td>{p.type}</td>
                    <td>{p.days}</td>
                    <td>{p.startTime}</td>
                    <td>{p.duration ?? ""}</td>
                    <td>{p.room}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="action-buttons">
            <button onClick={saveAll} className="btn btn-primary">
              Save to My Schedule
            </button>
          </div>
        </div>
      )}

      {msg.text && (
        <div className={`status-message ${msg.type}`}>
          <p>{msg.text}</p>
          {msg.type === "success" && (
            <button
              className="btn btn-secondary"
              onClick={() => nav("/app/react/my-schedule")}
            >
              View My Schedule
            </button>
          )}
        </div>
      )}
    </div>
  );
}
